import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const PLAN_STYLE = {
  free:     { bg: '#f8fafc', text: '#64748b', label: 'Free' },
  team:     { bg: '#eff6ff', text: '#2563eb', label: 'Team' },
  business: { bg: '#f5f3ff', text: '#7c3aed', label: 'Business' },
};

export default function SuperAdminPage() {
  const [accounts,   setAccounts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [changingPlanId, setChangingPlanId] = useState(null);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [accountUsers,    setAccountUsers]    = useState({}); // { account_id: [users] }
  const [loadingUsers,    setLoadingUsers]    = useState(false);
  const [deletingUserId,  setDeletingUserId]  = useState(null);

  const toggleAccountUsers = async (accountId) => {
    if (expandedAccount === accountId) { setExpandedAccount(null); return; }
    setExpandedAccount(accountId);
    if (accountUsers[accountId]) return; // already fetched
    setLoadingUsers(true);
    try {
      const res = await api.get(`/gpf/v1/admin/account-users?account_id=${accountId}`);
      setAccountUsers(prev => ({ ...prev, [accountId]: res.data }));
    } catch (e) {
      console.error('Failed to load account users:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (userId, userName, accountId) => {
    if (!window.confirm(`Permanently delete "${userName}"? This cannot be undone.`)) return;
    setDeletingUserId(userId);
    try {
      await api.delete(`/wp/v2/users/${userId}?force=true&reassign=1`);
      setAccountUsers(prev => ({
        ...prev,
        [accountId]: (prev[accountId] || []).filter(u => u.id !== userId),
      }));
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete user.');
    } finally {
      setDeletingUserId(null);
    }
  };

  const [planChangeMsg,  setPlanChangeMsg]  = useState('');
  const [search,     setSearch]     = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [stats,      setStats]      = useState({ total: 0, free: 0, team: 0, business: 0 });
  const [limits,     setLimits]     = useState({
    free:     { projects: 2,  users: 1,  companies: 1, calendar: false, reports: false, ai_agent: false, permissions: false, documents: true, research_center: false },
    team:     { projects: -1, users: -1, companies: 3, calendar: true,  reports: true,  ai_agent: false, permissions: false, documents: true,  research_center: false },
    business: { projects: -1, users: -1, companies: -1, calendar: true,  reports: true,  ai_agent: true,  permissions: true,  documents: true,  research_center: true  },
  });
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsMsg,    setLimitsMsg]    = useState('');

  useEffect(() => {
    fetchAccounts();
    api.get('/gpf/v1/admin/plan-limits').then(r => setLimits(r.data)).catch(console.error);
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/gpf/v1/admin/accounts');
      const data = res.data.accounts || [];
      setAccounts(data);
      setStats({
        total:    data.length,
        free:     data.filter(a => a.plan === 'free').length,
        team:     data.filter(a => a.plan === 'team').length,
        business: data.filter(a => a.plan === 'business').length,
      });
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveLimits = async () => {
    setSavingLimits(true);
    try {
      const payload = {};
      ['free','team','business'].forEach(plan => {
        payload[`${plan}_projects`]    = limits[plan].projects;
        payload[`${plan}_users`]       = limits[plan].users;
        payload[`${plan}_companies`]   = limits[plan].companies;
        payload[`${plan}_calendar`]    = limits[plan].calendar;
        payload[`${plan}_reports`]     = limits[plan].reports;
        payload[`${plan}_ai_agent`]    = limits[plan].ai_agent;
        payload[`${plan}_permissions`] = limits[plan].permissions;
        payload[`${plan}_documents`]   = limits[plan].documents;
        payload[`${plan}_research_center`] = limits[plan].research_center;
      });
      await api.post('/gpf/v1/admin/plan-limits', payload);
      localStorage.removeItem('gpf_plan_features'); // clear cache so all users get fresh limits
      setLimitsMsg('Limits saved! Changes take effect on next page load.');
      setTimeout(() => setLimitsMsg(''), 3000);
    } catch(e) { setLimitsMsg('Failed to save.'); }
    finally { setSavingLimits(false); }
  };

  const toggleFeature = (plan, feat) => {
    setLimits(prev => ({ ...prev, [plan]: { ...prev[plan], [feat]: !prev[plan][feat] } }));
  };

  const updateLimit = (plan, field, val) => {
    const num = val === '' || val === '-1' ? -1 : Math.max(1, parseInt(val) || 1);
    setLimits(prev => ({ ...prev, [plan]: { ...prev[plan], [field]: num } }));
  };

  const handleChangePlan = async (userId, newPlan) => {
    setChangingPlanId(userId);
    setPlanChangeMsg('');
    // Optimistic update
    setAccounts(prev => prev.map(a => a.id === userId ? { ...a, plan: newPlan } : a));
    try {
      await api.post('/gpf/v1/admin/change-plan', { user_id: userId, plan: newPlan });
      setPlanChangeMsg(`✅ Plan updated to ${newPlan.charAt(0).toUpperCase() + newPlan.slice(1)}`);
      setTimeout(() => setPlanChangeMsg(''), 3000);
    } catch (e) {
      setPlanChangeMsg('⚠️ Failed to update plan. Please try again.');
      fetchAccounts(); // resync on failure
    } finally {
      setChangingPlanId(null);
    }
  };

  const daysLeft = (trial_end) => {
    if (!trial_end) return null;
    const days = Math.ceil((new Date(trial_end) - new Date()) / (1000*60*60*24));
    return days;
  };

  const filtered = accounts.filter(a => {
    if (filterPlan !== 'all' && a.plan !== filterPlan) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!a.company?.toLowerCase().includes(q) && !a.email?.toLowerCase().includes(q) && !a.slug?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Platform Admin</h1>
            <p style={styles.sub}>All registered accounts on GoProjectFlow</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {planChangeMsg && <span style={{ fontSize: 13, fontWeight: 600, color: planChangeMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{planChangeMsg}</span>}
            <button style={styles.refreshBtn} onClick={fetchAccounts}>↻ Refresh</button>
          </div>
        </div>

        {/* Stats */}
        <div style={styles.statsGrid}>
          {[
            { label: 'Total Accounts', value: stats.total, color: '#2563eb', bg: '#eff6ff' },
            { label: 'Free Accounts',  value: stats.free,  color: '#64748b', bg: '#f8fafc' },
            { label: 'Team Accounts',  value: stats.team,  color: '#2563eb', bg: '#eff6ff' },
            { label: 'Business',       value: stats.business, color: '#7c3aed', bg: '#f5f3ff' },
          ].map(s => (
            <div key={s.label} style={{ ...styles.statCard, background: s.bg }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Plan Limits Management */}
        <div style={{ background: 'white', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Plan Limits</h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '2px 0 0' }}>Set -1 for unlimited</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {limitsMsg && <span style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>{limitsMsg}</span>}
              <button style={{ padding: '8px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }} onClick={saveLimits} disabled={savingLimits}>
                {savingLimits ? 'Saving...' : 'Save Limits'}
              </button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {['free', 'team', 'business'].map(plan => {
              const ps = { free: { color: '#64748b', bg: '#f8fafc' }, team: { color: '#2563eb', bg: '#eff6ff' }, business: { color: '#7c3aed', bg: '#f5f3ff' } }[plan];
              return (
                <div key={plan} style={{ background: ps.bg, borderRadius: 10, padding: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: ps.color, textTransform: 'capitalize', marginBottom: 12 }}>{plan} Plan</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>MAX PROJECTS</label>
                      <input type="number" value={limits[plan].projects} onChange={e => updateLimit(plan, 'projects', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} placeholder="-1 = unlimited" />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>MAX USERS</label>
                      <input type="number" value={limits[plan].users} onChange={e => updateLimit(plan, 'users', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} placeholder="-1 = unlimited" />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>MAX COMPANIES</label>
                      <input type="number" value={limits[plan].companies} onChange={e => updateLimit(plan, 'companies', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} placeholder="-1 = unlimited" />
                    </div>
                    <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 10, marginTop: 2 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 8 }}>FEATURES</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[
                          { key: 'documents',   label: '📄 Documents' },
                          { key: 'calendar',    label: '📅 Calendar & Meetings' },
                          { key: 'reports',     label: '📊 Reports' },
                          { key: 'ai_agent',    label: '🤖 AI-Auto Project/Task Builder' },
                          { key: 'research_center', label: '🔎 Research Center' },
                          { key: 'permissions', label: '🔐 Role Permissions' },
                        ].map(f => (
                          <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                            <div
                              onClick={() => toggleFeature(plan, f.key)}
                              style={{ width: 36, height: 20, borderRadius: 10, background: limits[plan][f.key] ? ps.color : '#e2e8f0', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}
                            >
                              <div style={{ position: 'absolute', top: 2, left: limits[plan][f.key] ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            </div>
                            <span style={{ color: limits[plan][f.key] ? '#0f172a' : '#94a3b8' }}>{f.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div style={styles.filterBar}>
          <div style={styles.tabs}>
            {['all','free','team','business'].map(p => (
              <button key={p} style={{ ...styles.tab, ...(filterPlan === p ? styles.tabActive : {}) }} onClick={() => setFilterPlan(p)}>
                {p === 'all' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          <input style={styles.search} placeholder="Search by company, email or slug..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Table */}
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span style={{ flex: 2 }}>Company</span>
            <span style={{ flex: 2 }}>Email</span>
            <span style={{ flex: 1.5 }}>Workspace URL</span>
            <span style={{ flex: 1.2 }}>Plan</span>
            <span style={{ flex: 1 }}>Trial</span>
            <span style={{ flex: 0.6, textAlign: 'center' }}>Projects</span>
            <span style={{ flex: 0.6, textAlign: 'center' }}>Tasks</span>
            <span style={{ flex: 0.6, textAlign: 'center' }}>Users</span>
            <span style={{ flex: 1 }}>Registered</span>
          </div>

          {loading && <p style={{ padding: 24, color: '#94a3b8', textAlign: 'center' }}>Loading accounts...</p>}
          {!loading && filtered.length === 0 && <p style={{ padding: 24, color: '#94a3b8', textAlign: 'center' }}>No accounts found.</p>}

          {filtered.map(account => {
            const ps    = PLAN_STYLE[account.plan] || PLAN_STYLE.free;
            const days  = daysLeft(account.trial_end);
            const isExp = days !== null && days <= 0;
            const isChangingThis = changingPlanId === account.id;

            return (
              <React.Fragment key={account.id}>
              <div style={styles.row}>
                <span style={{ flex: 2, fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{account.company || '—'}</span>
                <span style={{ flex: 2, fontSize: 13, color: '#64748b' }}>{account.email}</span>
                <span style={{ flex: 1.5, fontSize: 12, color: '#2563eb', fontFamily: 'monospace' }}>
                  {account.slug ? `${account.slug}.goprojectflow.com` : '—'}
                </span>
                <span style={{ flex: 1.2 }}>
                  <select
                    value={account.plan}
                    disabled={isChangingThis}
                    onChange={e => handleChangePlan(account.id, e.target.value)}
                    style={{
                      fontSize: 12, fontWeight: 700, padding: '4px 8px', borderRadius: 8,
                      background: ps.bg, color: ps.text, border: `1.5px solid ${ps.text}33`,
                      cursor: isChangingThis ? 'wait' : 'pointer', opacity: isChangingThis ? 0.6 : 1,
                    }}
                  >
                    <option value="free">Free</option>
                    <option value="team">Team</option>
                    <option value="business">Business</option>
                  </select>
                </span>
                <span style={{ flex: 1, fontSize: 12, color: isExp ? '#dc2626' : days !== null && days <= 7 ? '#d97706' : '#16a34a' }}>
                  {account.plan === 'free' ? 'Forever' : days === null ? '—' : isExp ? 'Expired' : `${days}d left`}
                </span>
                <span style={{ flex: 0.6, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>{account.projects}</span>
                <span style={{ flex: 0.6, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>{account.tasks}</span>
                <span
                  style={{ flex: 0.6, textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => toggleAccountUsers(account.account_id)}
                  title="Click to view and manage users"
                >
                  {account.users} {expandedAccount === account.account_id ? '▾' : '▸'}
                </span>
                <span style={{ flex: 1, fontSize: 12, color: '#94a3b8' }}>{new Date(account.registered).toLocaleDateString()}</span>
              </div>

              {expandedAccount === account.account_id && (
                <div style={{ background: '#f8fafc', padding: '14px 20px', borderBottom: '1px solid #e2e8f0' }}>
                  {loadingUsers && !accountUsers[account.account_id] ? (
                    <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Loading users...</p>
                  ) : (accountUsers[account.account_id] || []).length === 0 ? (
                    <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No users found.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {accountUsers[account.account_id].map(u => (
                        <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'white', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                          <span style={{ flex: 2, fontSize: 13, fontWeight: 600 }}>
                            {u.name}{u.is_owner && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', padding: '2px 7px', borderRadius: 99 }}>OWNER</span>}
                          </span>
                          <span style={{ flex: 2, fontSize: 12, color: '#64748b' }}>{u.email}</span>
                          <span style={{ flex: 1, fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{u.role.replace('gpf_', '').replace('_', ' ')}</span>
                          <span style={{ flex: 1, fontSize: 11, color: '#94a3b8' }}>{new Date(u.registered).toLocaleDateString()}</span>
                          <button
                            style={{ padding: '5px 12px', border: '1.5px solid #fecaca', borderRadius: 6, background: 'white', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => handleDeleteUser(u.id, u.name, account.account_id)}
                            disabled={deletingUserId === u.id}
                          >
                            {deletingUserId === u.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}

const styles = {
  page:        { maxWidth: 1200, margin: '0 auto' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title:       { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:         { fontSize: 14, color: '#64748b', margin: '4px 0 0' },
  refreshBtn:  { padding: '8px 16px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 },
  statsGrid:   { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  statCard:    { borderRadius: 12, padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  filterBar:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' },
  tabs:        { display: 'flex', gap: 4, background: 'white', padding: 4, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  tab:         { padding: '7px 14px', border: 'none', borderRadius: 6, background: 'transparent', fontSize: 13, fontWeight: 500, color: '#64748b', cursor: 'pointer' },
  tabActive:   { background: '#2563eb', color: 'white', fontWeight: 600 },
  search:      { padding: '9px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', width: 300 },
  table:       { background: 'white', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' },
  tableHeader: { display: 'flex', alignItems: 'center', padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', gap: 8 },
  row:         { display: 'flex', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #f1f5f9', gap: 8 },
};